import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Target, Plus, ArrowRight, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, StatusBadge, EmptyState, Spinner, Pill } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FIELDS, URGENCIES, fmtDate } from "@/lib/constants";

export default function EmployeeGoals() {
  const { t } = useTranslation();
  const [needs, setNeeds] = useState(null);
  const [form, setForm] = useState({ title: "", field: FIELDS[0], objective: "", urgency: "flexible" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/needs/mine").then(({ data }) => setNeeds(data)).catch(() => setNeeds([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/needs", form);
      toast.success(t("emp.goals.toastSubmitted"));
      setForm({ title: "", field: FIELDS[0], objective: "", urgency: "flexible" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <PageHeader eyebrow={t("emp.eyebrow")} title={t("emp.goals.title")} description={t("emp.goals.subtitle")} />

      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 items-start">
        <form onSubmit={submit} className="card-milled rounded-[0.8rem] p-6 sm:p-7 animate-fade-up">
          <div className="flex items-center gap-3 mb-5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><Sparkles size={17} strokeWidth={1.75} /></span>
            <div>
              <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{t("emp.goals.defineNew")}</h3>
              <p className="text-xs text-muted-foreground">{t("emp.goals.defineNewSub")}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("emp.goals.whatAchieve")}</Label>
              <Input data-testid="goal-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("emp.goals.whatAchievePlaceholder")} className="h-11 rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("emp.goals.focusArea")}</Label>
                <Select value={form.field} onValueChange={(v) => setForm({ ...form, field: v })}>
                  <SelectTrigger data-testid="goal-field" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELDS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("emp.goals.timeline")}</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="goal-urgency" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{URGENCIES.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("emp.goals.whatSuccess")}</Label>
              <Textarea data-testid="goal-objective" required value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder={t("emp.goals.whatSuccessPlaceholder")} className="rounded-lg min-h-28" />
            </div>
            <Button data-testid="goal-submit" type="submit" disabled={busy} className="w-full h-11 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
              {busy ? <Spinner /> : <><Plus size={16} /> {t("emp.goals.submitApproval")}</>}
            </Button>
          </div>
        </form>

        <div className="animate-fade-up d2">
          <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground mb-4">{t("emp.goals.yourGoals")}</h3>
          {needs === null ? (
            <div className="grid place-items-center py-16"><Spinner className="text-primary w-6 h-6" /></div>
          ) : needs.length === 0 ? (
            <EmptyState icon={Target} title={t("emp.goals.noGoals")} description={t("emp.goals.noGoalsDesc")} />
          ) : (
            <div className="space-y-3">
              {needs.map((n) => (
                <div key={n.need_id} data-testid={`goal-${n.need_id}`} className="card-milled rounded-[0.8rem] p-5 sm:p-6 transition-colors hover:border-primary/30">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium flex-1">{n.title}</h4>
                    <StatusBadge status={n.status} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{n.objective}</p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Pill className="bg-primary/10 text-primary">{n.field}</Pill>
                    <span className="text-xs text-muted-foreground">{t("emp.goals.submitted", { date: fmtDate(n.created_at) })}</span>
                    {n.status === "approved" && (
                      <Link to="/experts" className="ml-auto"><Button size="sm" className="rounded-lg gap-1.5" data-testid={`goal-book-${n.need_id}`}>{t("emp.goals.bookExpert")} <ArrowRight size={14} /></Button></Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
